import React, { FC, useEffect, useState } from 'react';
import { get } from 'lodash';
import loadable from '@loadable/component';
import { useTranslation } from 'react-i18next';
import { useQueryParam } from 'use-query-params';

import Menu from 'vibo-ui/Menu';
import Modal from 'vibo-ui/Modal';
import Wizard from 'vibo-ui/Wizard';
import Button from 'vibo-ui/Button';
import SearchInput from 'vibo-ui/SearchInput';
import { useWizard, WizardState } from 'vibo-ui/Wizard/WizardStepContext';

import { ASSIGN_PLAYLIST_MENU, assigneViews, DROP_SEARCH_Q_STEPS } from './constants';

import { Modals } from 'types/enums';
import { AssignPlaylistModalProps, AssigneType } from './interfaces';

const Events = loadable(() => import('./listView/Events')) as FC;
const Templates = loadable(() => import('./listView/Templates')) as FC;
const TemplateSections = loadable(() => import('./sectionsView/TemplateSections')) as FC;
const FavoriteSections = loadable(() => import('./sectionsView/FavoriteSections')) as FC;
const EventSections = loadable(() => import('./sectionsView/EventSections')) as FC;

const AssignPlaylistModalInner: FC<AssignPlaylistModalProps> = ({ assigneType, ...rest }) => {
  const { t } = useTranslation();

  const [lastTab, setLastTab] = useState<AssigneType>(assigneType);

  const { wizardRefProps } = useWizard();

  const [assignQ, setAssignQ] = useQueryParam<string | undefined>('assignQ');
  const [, setOption] = useQueryParam<string | undefined>('optionId');
  const [title, setTitle] = useQueryParam<string | undefined>('title');

  const handleBack = () => {
    clearParams();
    wizardRefProps?.goToNamedStep(lastTab);
  };

  const initialStep = Object.values(AssigneType).findIndex(value => value === assigneType);
  const currentStep = (wizardRefProps?.stepName as AssigneType) ?? assigneType;

  const searchPlaceholder = get(assigneViews, `${currentStep}.searchText`, 'searchSection');

  const clearParams = () => {
    setAssignQ(undefined);
    setTitle(undefined);
    setOption(undefined);
  };

  useEffect(() => {
    return () => {
      clearParams();
    };
  }, []);

  useEffect(() => {
    if (
      !!wizardRefProps?.stepName &&
      DROP_SEARCH_Q_STEPS.includes(wizardRefProps?.stepName as AssigneType)
    ) {
      setAssignQ(undefined);
    }
  }, [wizardRefProps?.stepName]);

  return (
    <Modal modalName={Modals.assignPlaylist} title={t('assignPlaylistTo')} view="side" {...rest}>
      <div className="headers">
        <Modal.Header>
          <SearchInput
            value={assignQ}
            onChange={(_, q) => setAssignQ(q)}
            placeholder={t(searchPlaceholder)}
            queryName=""
            withClearBtn={false}
          />
        </Modal.Header>
        <Modal.Header>
          {!!title ? (
            <b>{title}</b>
          ) : (
            <Menu
              onSelect={({ key }) => {
                setLastTab(key as AssigneType);
                wizardRefProps?.goToNamedStep(key);
              }}
              defaultSelectedKeys={[lastTab]}
              styleModificator="siderTop"
            >
              {ASSIGN_PLAYLIST_MENU.map(option => (
                <Menu.Item eventKey={option.key} key={option.key}>
                  {t(option.name)}
                </Menu.Item>
              ))}
            </Menu>
          )}
        </Modal.Header>
      </div>
      <Modal.Body id="choose-list-scrollbar">
        <Wizard initialStep={initialStep + 1}>
          <Wizard.Step stepName={AssigneType.event}>
            <Events />
          </Wizard.Step>
          <Wizard.Step stepName={AssigneType.template}>
            <Templates />
          </Wizard.Step>
          <Wizard.Step stepName={AssigneType.eventSections}>
            <EventSections />
          </Wizard.Step>
          <Wizard.Step stepName={AssigneType.templateSections}>
            <TemplateSections />
          </Wizard.Step>
          <Wizard.Step stepName={AssigneType.favoriteSections}>
            <FavoriteSections />
          </Wizard.Step>
        </Wizard>
      </Modal.Body>
      {!!title ? (
        <Modal.Footer>
          <Button onClick={handleBack} displayType="bordered" shape="round" size="lg">
            {t('back')}
          </Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};

const AssignPlaylistModal: FC<AssignPlaylistModalProps> = ({ ...rest }) => (
  <WizardState>
    <AssignPlaylistModalInner {...rest} />
  </WizardState>
);

export default AssignPlaylistModal;
